/**
 * JsonTreeView - Interactive expandable JSON tree viewer
 * 
 * Browser DevTools-style JSON viewer with:
 * - Click to expand/collapse objects and arrays
 * - Syntax highlighting for types
 * - Copy value on click
 * - Configurable initial expansion depth
 * 
 * @example
 * <JsonTreeView 
 *   data={{ foo: "bar", nested: { a: 1 } }} 
 *   initialExpandDepth={1}
 * />
 */

import React, { useState, useCallback, useMemo } from 'react';
import { ChevronRight, Copy, Check } from 'lucide-react';

interface JsonTreeViewProps {
  data: unknown;
  /** How many levels deep to expand by default (0 = collapsed, Infinity = all) */
  initialExpandDepth?: number;
  /** Root key name (for display) */
  rootKey?: string;
  /** Whether this is the root level (internal) */
  isRoot?: boolean;
  /** Current depth (internal) */
  depth?: number;
}

type JsonValueType = 'string' | 'number' | 'boolean' | 'null' | 'undefined' | 'object' | 'array';

function getValueType(value: unknown): JsonValueType {
  if (value === null) return 'null';
  if (value === undefined) return 'undefined';
  if (Array.isArray(value)) return 'array';
  return typeof value as JsonValueType;
}

function getTypeColor(type: JsonValueType): string {
  switch (type) {
    case 'string': return 'text-green-600 dark:text-green-400';
    case 'number': return 'text-blue-600 dark:text-blue-400';
    case 'boolean': return 'text-purple-600 dark:text-purple-400';
    case 'null': return 'text-gray-500 dark:text-gray-500';
    case 'undefined': return 'text-gray-500 dark:text-gray-500';
    default: return 'text-gray-700 dark:text-gray-300';
  }
}

function formatValue(value: unknown, type: JsonValueType): string {
  switch (type) {
    case 'string': return `"${value}"`;
    case 'null': return 'null';
    case 'undefined': return 'undefined';
    case 'boolean': return String(value);
    case 'number': return String(value);
    default: return '';
  }
}

function getPreviewText(value: unknown, type: JsonValueType): string {
  if (type === 'array') {
    const arr = value as unknown[];
    if (arr.length === 0) return '[]';
    if (arr.length <= 3) {
      const preview = arr.map(v => {
        const vType = getValueType(v);
        if (vType === 'object') return '{...}';
        if (vType === 'array') return '[...]';
        if (vType === 'string') return `"${String(v).slice(0, 20)}${String(v).length > 20 ? '...' : ''}"`;
        return String(v);
      }).join(', ');
      return `[${preview}]`;
    }
    return `Array(${arr.length})`;
  }
  if (type === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    if (keys.length === 0) return '{}';
    if (keys.length <= 3) {
      return `{${keys.join(', ')}}`;
    }
    return `{${keys.slice(0, 2).join(', ')}, ...}`;
  }
  return '';
}

// Individual key-value row component
function JsonNode({ 
  keyName, 
  value, 
  depth, 
  initialExpandDepth,
  isLast = false,
}: { 
  keyName: string | number;
  value: unknown; 
  depth: number;
  initialExpandDepth: number;
  isLast?: boolean;
}) {
  const type = getValueType(value);
  const isExpandable = type === 'object' || type === 'array';
  const [isExpanded, setIsExpanded] = useState(depth < initialExpandDepth);
  const [copied, setCopied] = useState(false);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isExpandable) {
      setIsExpanded(!isExpanded);
    }
  }, [isExpandable, isExpanded]);

  const handleCopyValue = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const textToCopy = JSON.stringify(value, null, 2);
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [value]);

  const childEntries = useMemo(() => {
    if (!isExpandable || !isExpanded) return [];
    if (type === 'array') {
      return (value as unknown[]).map((v, i) => ({ key: i, value: v }));
    }
    return Object.entries(value as Record<string, unknown>).map(([k, v]) => ({ key: k, value: v }));
  }, [value, type, isExpandable, isExpanded]);

  const indent = depth * 16; // 16px per level

  return (
    <div className="font-mono text-xs">
      {/* Key-Value Row */}
      <div 
        className="flex items-center group hover:bg-gray-100 dark:hover:bg-gray-800 rounded px-1 py-0.5 cursor-pointer"
        style={{ paddingLeft: `${indent}px` }}
        onClick={handleToggle}
      >
        {/* Expand/Collapse Arrow */}
        <span className="w-4 h-4 flex-shrink-0 flex items-center justify-center">
          {isExpandable ? (
            <ChevronRight 
              className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
          ) : (
            <span className="w-3" />
          )}
        </span>

        {/* Key */}
        <span className="text-purple-700 dark:text-purple-400 mr-1">
          {typeof keyName === 'string' ? `"${keyName}"` : keyName}
        </span>
        <span className="text-gray-500 mr-1">:</span>

        {/* Value or Preview */}
        {isExpandable ? (
          <span className="text-gray-500 dark:text-gray-400">
            {isExpanded ? (type === 'array' ? '[' : '{') : getPreviewText(value, type)}
          </span>
        ) : (
          <span className={getTypeColor(type)}>
            {formatValue(value, type)}
          </span>
        )}

        {/* Copy button (shown on hover) */}
        <button
          onClick={handleCopyValue}
          className="ml-2 opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-opacity"
          title="Copy value"
        >
          {copied ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <Copy className="w-3 h-3" />
          )}
        </button>

        {/* Item count for collapsed expandables */}
        {isExpandable && !isExpanded && (
          <span className="ml-1 text-gray-400 text-[10px]">
            {type === 'array' 
              ? `${(value as unknown[]).length} items`
              : `${Object.keys(value as object).length} keys`
            }
          </span>
        )}
      </div>

      {/* Children (expanded) */}
      {isExpandable && isExpanded && (
        <>
          {childEntries.map((entry, idx) => (
            <JsonNode
              key={entry.key}
              keyName={entry.key}
              value={entry.value}
              depth={depth + 1}
              initialExpandDepth={initialExpandDepth}
              isLast={idx === childEntries.length - 1}
            />
          ))}
          {/* Closing bracket */}
          <div 
            className="text-gray-500 dark:text-gray-400 py-0.5"
            style={{ paddingLeft: `${indent}px` }}
          >
            {type === 'array' ? ']' : '}'}
            {!isLast && ','}
          </div>
        </>
      )}
    </div>
  );
}

export function JsonTreeView({ 
  data, 
  initialExpandDepth = 1,
  rootKey,
  isRoot = true,
  depth = 0,
}: JsonTreeViewProps) {
  const type = getValueType(data);
  const isExpandable = type === 'object' || type === 'array';
  const [isExpanded, setIsExpanded] = useState(depth < initialExpandDepth);
  const [copied, setCopied] = useState(false);

  const handleCopyAll = useCallback(() => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [data]);

  // For primitive values at root
  if (!isExpandable) {
    return (
      <div className="font-mono text-xs p-2">
        <span className={getTypeColor(type)}>{formatValue(data, type)}</span>
      </div>
    );
  }

  const entries = type === 'array' 
    ? (data as unknown[]).map((v, i) => ({ key: i, value: v }))
    : Object.entries(data as Record<string, unknown>).map(([k, v]) => ({ key: k, value: v }));

  return (
    <div className="bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header with copy all button */}
      {isRoot && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
            {type === 'array' ? `Array (${entries.length})` : `Object (${entries.length} keys)`}
          </span>
          <button
            onClick={handleCopyAll}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            title="Copy all"
          >
            {copied ? (
              <>
                <Check className="w-3 h-3 text-green-500" />
                <span className="text-green-500">Copied!</span>
              </>
            ) : (
              <>
                <Copy className="w-3 h-3" />
                <span>Copy All</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Tree content */}
      <div className="p-2 max-h-80 overflow-auto">
        {entries.map((entry, idx) => (
          <JsonNode
            key={entry.key}
            keyName={entry.key}
            value={entry.value}
            depth={0}
            initialExpandDepth={initialExpandDepth}
            isLast={idx === entries.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export default JsonTreeView;
